import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { DollarSign, FileSpreadsheet, Image, Loader2, MessageSquare, Printer, Save, SlidersHorizontal, ZoomIn, ZoomOut } from 'lucide-react';
import { useTheme, ThemeContext } from '../../../contexts/ThemeContext';
import { useAuth } from '../../../contexts/AuthContext';
import { getAllTemplates } from '../../../templates/index';
import { getReportTypeFromTemplateName } from '../../../utils/reportFieldConfig';
import { reportModalPanelClass, reportModalBorderClass, reportModalHeaderBgClass } from '../reportTheme';
import ReportDisplayModal from '../components/ReportDisplayModal';
import ContinuousReportViewer from '../components/ContinuousReportViewer';
import ReportConfigDialog from '../components/ReportConfigDialog';
import FieldInsightPanel from '../../../components/FieldInsightPanel';
import * as fieldInsightStore from '../../../utils/fieldInsightStore';
import { buildReportSlice } from '../lib/reportSlice';
import { paginateReport } from '../lib/paginate';
import { exportReportToXlsx } from '../lib/reportExport';
import { createSavedTemplate } from '../lib/reportTemplatesApi';
import { ZOOM_RESET, canZoomIn, canZoomOut, stepZoom, zoomPercent } from '../lib/reportZoom';
import { groupSigil } from '../lib/configLabel';
import { buildReportRequestParams } from '../lib/reportRequest';
import { allowedIndexNames } from '../lib/benchmarkIndexGroups';
import { buildReportEnvelope, createCancellableReportFetch, newCancelToken } from '../lib/generateReport';
import { addRecentReport } from '../lib/recentReports';
import { rememberGeneratedDates } from '../lib/lastGeneratedDates';
import { reportTitleForType } from '../lib/reportTitle';
import { useEscapeKey } from '../lib/useEscapeKey';
import { getFirmBranding } from '../../manual/lib/firmBranding';
import { useReportWindows } from './ReportWindowsContext';
import { copyStylesToPopup, setPopupBodyTheme, watchPopupClose, movePrintPagesToPopup, cleanupPopupPrint } from './popupWindow';

function getPrintRoot() {
  let el = document.getElementById('report-print-root');
  if (!el) {
    el = document.createElement('div');
    el.id = 'report-print-root';
    document.body.appendChild(el);
  }
  return el;
}

/** Subtitle line for the titlebar: account name or @group / @@group.
 * The sigil comes from what the USER requested (requestParams.group_mode), not
 * the envelope's mode: three report types force the aggregate path even for a
 * plain @. The envelope's group.consolidated is the fallback for windows
 * whose requestParams predate the sigil (cached reopens). */
function windowSubtitle(reportData, requestParams) {
  if (!reportData) return '';
  const group = reportData.__group;
  if (group && group.codename) {
    const sigil = groupSigil(requestParams?.group_mode ?? group.consolidated);
    const seq = Array.isArray(reportData.__reports) ? ` · ${reportData.__reports.length} accounts` : '';
    return `${sigil}${group.codename}${group.name ? ` - ${group.name}` : ''}${seq}`;
  }
  const header = reportData.header || {};
  return header.account_name || header.account_code || '';
}

const ReportPopup = ({ win }) => {
  const { darkMode } = useTheme();
  const { fetchWithAuth, recordActivity } = useAuth();
  const { closeReportWindow, getHandle, rerunReportWindow } = useReportWindows();
  const popupWin = getHandle(win.id);

  // Wire this popup's OS window to React: copy styles once, keep its shell theme
  // in sync, and prune the descriptor when the user closes the OS window.
  const styledRef = useRef(false);
  useEffect(() => {
    if (!popupWin || popupWin.closed) return undefined;
    if (!styledRef.current) { copyStylesToPopup(popupWin); styledRef.current = true; }
    setPopupBodyTheme(popupWin, darkMode);
    const stop = watchPopupClose(popupWin, () => closeReportWindow(win.id));
    return stop;
  }, [popupWin, darkMode, win.id, closeReportWindow]);

  // Active group member = the portfolio currently under the frozen header, driven
  // by the viewer's scroll-spy (and set instantly on a tab click). All members
  // render at once, so this only highlights the active tab and provides a fallback
  // for field-insight attribution when a click can't identify the portfolio itself.
  const [activeMember, setActiveMember] = useState(0);
  // Tab click -> ask the viewer to scroll to that portfolio. Bump the nonce each
  // time so repeat clicks re-scroll; the tab is a jump control, not a content swap.
  const scrollNonce = useRef(0);
  const [scrollRequest, setScrollRequest] = useState(null);
  const scrollToMember = useCallback((idx) => {
    scrollNonce.current += 1;
    setScrollRequest({ idx, nonce: scrollNonce.current });
    setActiveMember(idx);
  }, []);
  // Expanded lot-detail rows (realized/unrealized gains), scoped PER MEMBER
  // ({ [memberIndex]: { [rowKey]: true } }) so the same security expanded in one
  // portfolio doesn't read as expanded in another. The viewer's delegated handler
  // reports (memberIndex, rowKey) and the LIVE report re-renders in place - no
  // re-pagination. Controlled here so a double-click-to-explain can skip the toggle.
  const [expandedByMember, setExpandedByMember] = useState({});
  const toggleRow = useCallback(
    (idx, key) => setExpandedByMember((prev) => ({
      ...prev,
      [idx]: { ...(prev[idx] || {}), [key]: !(prev[idx] && prev[idx][key]) },
    })),
    [],
  );
  const toggleAll = useCallback(
    (idx, keys, expand) => setExpandedByMember((prev) => ({
      ...prev,
      [idx]: expand ? Object.fromEntries(keys.map((k) => [k, true])) : {},
    })),
    [],
  );
  const [printing, setPrinting] = useState(false);
  // Yield-on-cost basis + its market-yield companion + the Portfolio Summary
  // cash-blend toggle live HERE, not in ReportDisplayModal: the print sources
  // below are fresh embedded instances, so component-local state would reset
  // them to the defaults and print would ignore the on-screen selection.
  const [yocBasis, setYocBasis] = useState('ytm');
  const [showMarketYield, setShowMarketYield] = useState(false);
  const [includeCashYield, setIncludeCashYield] = useState(false);
  // Net-of-fees basis, lifted for the same reason: the print/export sources
  // below are fresh embedded instances, so a component-local basis would print
  // GROSS while the screen showed NET. Default 'gross' (PM ruling).
  const [feeBasis, setFeeBasis] = useState('gross');
  // In-report security lookup for the transaction listings (viewer state; the
  // print and export mounts below receive the value only, so what you see is
  // what prints/exports - the input control itself is data-*-skip stripped).
  const [securityFilter, setSecurityFilter] = useState('');
  // Accrued-interest basis (settlement date vs trade date), lifted for the same
  // reason. Default 'settle' - the figure that ties APX today.
  const [accruedBasis, setAccruedBasis] = useState('settle');
  // Holdings basis (settlement date vs trade date), lifted for the same reason.
  // Default 'trade' - BanyanAI is a trade-date system.
  const [holdingsBasis, setHoldingsBasis] = useState('trade');
  // Return basis (time-weighted vs IRR), lifted for the same reason. Default
  // 'twr' - the industry-standard headline; IRR is the toggle.
  const [returnBasis, setReturnBasis] = useState('twr');
  // Benchmark hide is ephemeral and per-window: it lives only as long as this
  // rendered report. A regeneration replaces win.reportData, and the effect
  // below clears the set so every benchmark returns (with its hide option).
  const [hiddenIndexes, setHiddenIndexes] = useState(() => new Set());
  const toggleHiddenIndex = useCallback((key) => {
    setHiddenIndexes((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);
  useEffect(() => { setHiddenIndexes(new Set()); }, [win.reportData]);
  // Persistent benchmark selection (from the generation dialog) rides on
  // requestParams. Absent => null (no restriction); present => allow-set of
  // index names. Unlike hiddenIndexes this does not reset on regeneration.
  const selectedIndexNames = useMemo(() => {
    const groups = win.requestParams?.selected_index_groups;
    return groups == null ? null : allowedIndexNames(groups);
  }, [win.requestParams]);
  // Cents vs whole dollars for every money column, lifted for the same reason.
  // null = the PM has not touched the toggle, so the report opens at the default.
  //
  // WHOLE DOLLARS IS THAT DEFAULT, for every report (PM directive 2026-09-17).
  // It used to depend on the report type - a handful of families opened whole and
  // the rest opened with cents - which meant the toolbar's first click did
  // something different depending on which report you had open.
  //
  // This is DISPLAY ONLY. The spreadsheet still carries full cents: the export
  // renders its own hidden copy at "cents" regardless of what the screen shows,
  // which is exactly why that copy exists.
  const [moneyOverride, setMoneyOverride] = useState(null);
  const moneyPrecision = moneyOverride || 'whole';
  // The toggle is DISPLAY ONLY - it must never cost the spreadsheet its cents.
  // reportExport walks the live DOM and coerces the rendered text, so exporting
  // while the screen shows whole dollars would write rounded numbers and the
  // cents would be unrecoverable from the sheet. When the screen is rounded we
  // export from a hidden full-precision copy instead (same trick print already
  // uses to escape the on-screen theme).
  const [exporting, setExporting] = useState(false);
  const [showLogo, setShowLogo] = useState(false);
  const [zoom, setZoom] = useState(ZOOM_RESET);

  // The firm's report branding (Manual > Report Branding): custom text or a logo,
  // printed in the top left of page 1. Read through the module cache, so it costs
  // one request per session no matter how many report windows are opened. A
  // failure is silent - branding stays null and the report renders as it always
  // has, which is strictly better than blocking a report on a decorative mark.
  const [brand, setBrand] = useState(null);
  useEffect(() => {
    let mounted = true;
    getFirmBranding(fetchWithAuth)
      .then((b) => { if (mounted) setBrand(b); })
      .catch(() => {});
    return () => { mounted = false; };
  }, [fetchWithAuth]);

  // "Rerun with different parameters": reopens THIS report's own configuration
  // dialog inside this window and regenerates in place. The dialog opens with
  // ONLY the last-generated DATE prefilled (lib/lastGeneratedDates - product
  // rule 2026-08-06) and every other field at its default, so the PM re-picks
  // a different portfolio for the same date without inheriting the rest.
  // The result replaces the report here (a click inside a popup gives the opener
  // no user activation, so a second window could not be opened reliably anyway).
  const [rerunOpen, setRerunOpen] = useState(false);
  const [rerunError, setRerunError] = useState(null);

  // "Save configuration" popover: only meaningful when this window carries
  // the originating generation request (win.requestParams, threaded through
  // by ReportWindowsContext.openReportWindow). Saves { name, reportType, params }
  // via POST /reports/templates so it re-appears in the Reports page "Saved"
  // rail (SavedConfigsRail.jsx).
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState(win.reportName || '');
  const [saveStatus, setSaveStatus] = useState('idle'); // idle | saving | error
  const [saveErrorMessage, setSaveErrorMessage] = useState('');
  const openSavePopover = useCallback(() => {
    setSaveName(win.reportName || '');
    setSaveStatus('idle');
    setSaveOpen((v) => !v);
  }, [win.reportName]);
  const handleSaveConfig = useCallback(async () => {
    const name = saveName.trim();
    if (!name || !win.requestParams) return;
    setSaveStatus('saving');
    try {
      await createSavedTemplate({ name, reportType: win.reportType, params: win.requestParams });
      setSaveStatus('idle');
      setSaveOpen(false);
    } catch (e) {
      setSaveStatus('error');
      setSaveErrorMessage((e && e.message) || 'Failed to save');
    }
  }, [saveName, win.requestParams, win.reportType]);

  // The dialog needs the catalog entry, and the window carries only a report
  // type - resolve it the way reportTitle.js does. Memoized: getAllTemplates()
  // scans the whole catalog, and this component re-renders on zoom/insight/
  // scroll-spy changes that never affect win.reportType.
  const template = useMemo(
    () => (win.reportType
      ? getAllTemplates().find((t) => getReportTypeFromTemplateName(t.name) === win.reportType)
      : null),
    [win.reportType],
  );

  const handleRerun = useCallback((params) => {
    const reportType = win.reportType;
    const reportName = win.reportName;
    const requestParams = buildReportRequestParams(params, {
      reportType,
      // Keep the originating client context for a report opened from a client
      // page, where no portfolio codename lands in the params.
      clientId: win.requestParams?.client_id ?? null,
    });
    rememberGeneratedDates(reportType, requestParams);
    setRerunOpen(false);
    setRerunError(null);
    const { fetchReport, cancel } = createCancellableReportFetch();
    rerunReportWindow(win.id, {
      onCancel: cancel,
      produce: async ({ onProgress }) => {
        const result = await fetchReport(reportType, requestParams, {
          token: newCancelToken(), onProgress,
        });
        addRecentReport({ reportType, reportName, requestParams });
        return { reportType, reportName, reportData: buildReportEnvelope(result, requestParams), requestParams };
      },
    })
      // The row keys point at data that has just been replaced.
      .then(() => setExpandedByMember({}))
      .catch((e) => setRerunError((e && e.message) || 'Failed to rerun report'));
  }, [win.id, win.reportType, win.reportName, win.requestParams, rerunReportWindow]);

  const closeRerun = useCallback(() => setRerunOpen(false), []);
  // Esc closes the dialog. Bound to the POPUP's document: this component runs in
  // the opener's realm, so the global `document` never sees the popup's keys.
  // `enabled` is also gated on having that document - without a usable popup
  // handle there is nothing to bind to, and passing `null` alone would make
  // useEscapeKey fall back to the global `document`, binding Esc to the
  // OPENER's window instead.
  const popupDocument = popupWin && !popupWin.closed ? popupWin.document : null;
  useEscapeKey(closeRerun, rerunOpen && !!popupDocument, popupDocument);

  // All reports render live in the continuous viewer; group "@" reports stack every
  // member. The viewer exposes its live content wrapper via `sourceRef`, which the
  // xlsx export reads (member-aware -> one sheet-set per portfolio).
  const sourceRef = useRef(null);
  const groupMembers = Array.isArray(win.reportData?.__reports) ? win.reportData.__reports : null;
  // Option flags the group shell folds into each member's data (mirrors the
  // chromeless render); also used to build the per-member print sources below.
  const optionFlags = {
    show_summary_information: win.reportData?.show_summary_information,
    show_distribution_by_moody_rating: win.reportData?.show_distribution_by_moody_rating,
    show_distribution_by_sp_rating: win.reportData?.show_distribution_by_sp_rating,
    show_distribution_by_maturity: win.reportData?.show_distribution_by_maturity,
  };
  // Members to print/paginate: every portfolio for a group "@" report, else the
  // single report as a one-member list (so the print loop below is uniform).
  const printMembers = groupMembers || [{ account_header: win.reportData?.header, data: win.reportData }];

  // Field-insight chat overlay. One session per window (sessionKey = win.id); its
  // store state survives minimize (the window stays mounted) and is erased only on
  // close (the cleanup effect below). focusNonce bumps on every field click so the
  // panel refocuses its input even when it's already open.
  const [insightOpen, setInsightOpen] = useState(false);
  const [focusNonce, setFocusNonce] = useState(0);

  // Rendered page-strip geometry reported by the viewer ({pageRightEdge,
  // viewportWidth}); drives where the insight panel strip starts so it fills and
  // centers in the space the report does not cover, tracking zoom/fit/resize.
  const [pageLayout, setPageLayout] = useState(null);
  const onPageLayout = useCallback((l) => {
    setPageLayout((prev) =>
      prev && prev.pageRightEdge === l.pageRightEdge && prev.viewportWidth === l.viewportWidth ? prev : l
    );
  }, []);

  // Builds the FieldContext for a clicked cell. account_code/account_name come from
  // the ACTIVE member header for group "@" reports, else the report header.
  // report_data ships the already-computed on-screen payload (the clicked row's
  // full record when identifiable, else the whole capped payload) so the chatbot
  // answers from the exact numbers on screen without re-querying the lake.
  const buildFieldContext = useCallback((cellCtx) => {
    const rd = win.reportData || {};
    // Attribute to the portfolio the clicked cell physically belongs to (its member
    // section index), falling back to the scroll-spy active member.
    const idx = groupMembers
      ? Math.min(cellCtx.memberIndex != null ? cellCtx.memberIndex : activeMember, groupMembers.length - 1)
      : 0;
    const activeData = groupMembers ? (rd.__reports?.[idx]?.data || {}) : rd;
    const header = groupMembers ? (rd.__reports?.[idx]?.account_header || {}) : (rd.header || {});
    const asOf = rd.asOfDate || rd.as_of_date || rd.endDate || rd.end_date || rd.date || null;
    return {
      surface: 'report',
      report_type: win.reportType || null,
      account_code: header.account_code || null,
      account_name: header.account_name || null,
      client_id: null,
      codename: null,
      as_of_date: asOf,
      section: cellCtx.section ?? null,
      column_label: cellCtx.column_label ?? null,
      row_description: cellCtx.row_description ?? null,
      // Marked non-table fields (data-insight-field, e.g. the summary tiles)
      // carry their label here instead of column/row coordinates.
      field_label: cellCtx.field_label ?? null,
      field_value: cellCtx.cell_value,
      field_value_raw: null,
      report_data: buildReportSlice(activeData, header, cellCtx),
    };
  }, [win.reportData, win.reportType, groupMembers, activeMember]);

  const onFieldSingleClick = useCallback((cellCtx) => {
    fieldInsightStore.setField(win.id, buildFieldContext(cellCtx));
    setInsightOpen(true);
    setFocusNonce((n) => n + 1);
  }, [win.id, buildFieldContext]);

  const onFieldDoubleClick = useCallback((cellCtx) => {
    const field = buildFieldContext(cellCtx);
    setInsightOpen(true);
    setFocusNonce((n) => n + 1);
    fieldInsightStore.startExplain(win.id, { fetchWithAuth, recordActivity, field });
  }, [win.id, buildFieldContext, fetchWithAuth, recordActivity]);

  // Erase-on-close: the window unmounts only when closed (minimize keeps it
  // mounted), so dropping the session on unmount clears history + draft on close
  // while preserving them across minimize/reopen.
  useEffect(() => () => fieldInsightStore.clearSession(win.id), [win.id]);

  // Print: paginate each portfolio's (forced-light) body into #report-print-root via
  // paged.js - the SAME landscape, header-per-page document as before. A group "@"
  // report prints EVERY member back-to-back (each paginated separately so it gets
  // its OWN per-page header), accumulated via `append`; page numbers run
  // continuously. A single report is one member with append:false - byte-identical.
  //
  // The finished page tree is then MOVED into the report's own OS window so the
  // print preview shows over THAT window, not the opener (home page). If the popup
  // is gone we fall back to printing the opener. Cleanup runs on afterprint and,
  // as a fallback, when this effect tears down (unmount / re-print).
  // Export from the hidden full-precision copy once it has mounted, then drop
  // it. One tick is enough - the portal renders synchronously with this commit.
  useEffect(() => {
    if (!exporting) return undefined;
    let cancelled = false;
    const t = setTimeout(() => {
      const src = document.getElementById('report-export-source');
      const done = () => { if (!cancelled) setExporting(false); };
      if (!src) { done(); return; }
      exportReportToXlsx(src, win.reportName)
        .catch((e) => console.error('Report export failed', e))
        .finally(done);
    }, 0);
    return () => { cancelled = true; clearTimeout(t); };
  }, [exporting, win.reportName]);

  useEffect(() => {
    if (!printing) return undefined;
    let cancelled = false;
    const printRoot = getPrintRoot();
    let cleaned = false;
    let detach = () => {};
    const cleanup = () => {
      if (cleaned) return;
      cleaned = true;
      detach();
      cleanupPopupPrint(popupWin);
      printRoot.innerHTML = '';
      setPrinting(false);
    };
    const t = setTimeout(async () => {
      try {
        for (let i = 0; i < printMembers.length; i += 1) {
          if (cancelled) break;
          const src = document.getElementById(`report-print-source-${i}`);
          if (src) await paginateReport(src, printRoot, { print: true, append: i > 0 });
        }
      } catch (e) {
        console.error('Print pagination failed', e); // eslint-disable-line no-console
      }
      if (cancelled) return;
      const usePopup = popupWin && !popupWin.closed && popupWin.document && typeof popupWin.print === 'function';
      const target = usePopup ? popupWin : window;
      if (usePopup) movePrintPagesToPopup(printRoot, popupWin);
      const onAfter = () => cleanup();
      target.addEventListener('afterprint', onAfter);
      detach = () => { try { target.removeEventListener('afterprint', onAfter); } catch (e) { /* ignore */ } };
      if (usePopup) popupWin.focus();
      target.print();
    }, 60);
    return () => {
      cancelled = true;
      clearTimeout(t);
      detach();
      cleanupPopupPrint(popupWin);
    };
  }, [printing]); // eslint-disable-line react-hooks/exhaustive-deps

  // The on-screen report body: all members stacked (chromeless). Row toggles are
  // driven by the viewer's delegated handler straight into ReportPopup, so the
  // body only needs the per-member expanded state to render lot rows.
  const reportBody = () => (
    <ReportDisplayModal
      chromeless
      isOpen
      onClose={() => closeReportWindow(win.id)}
      reportData={win.reportData}
      reportType={win.reportType}
      reportName={win.reportName}
      showLogo={showLogo}
      brand={brand}
      moneyPrecision={moneyPrecision}
      expandedByMember={expandedByMember}
      yocBasis={yocBasis}
      onYocBasisChange={setYocBasis}
      showMarketYield={showMarketYield}
      onShowMarketYieldChange={setShowMarketYield}
      includeCashYield={includeCashYield}
      onIncludeCashYieldChange={setIncludeCashYield}
      feeBasis={feeBasis}
      onFeeBasisChange={setFeeBasis}
      accruedBasis={accruedBasis}
      onAccruedBasisChange={setAccruedBasis}
      holdingsBasis={holdingsBasis}
      onHoldingsBasisChange={setHoldingsBasis}
      securityFilter={securityFilter}
      onSecurityFilterChange={setSecurityFilter}
      returnBasis={returnBasis}
      onReturnBasisChange={setReturnBasis}
      hiddenIndexes={hiddenIndexes}
      onToggleIndex={toggleHiddenIndex}
      selectedIndexNames={selectedIndexNames}
    />
  );

  const iconBtn = `p-1.5 rounded transition-colors ${
    darkMode ? 'text-gray-400 hover:text-white hover:bg-white/10' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-200'
  }`;

  const subtitle = windowSubtitle(win.reportData, win.requestParams);

  // Nothing to portal into if the OS window is gone.
  if (!popupWin || popupWin.closed) return null;
  const mount = popupWin.document.getElementById('report-popup-root');
  if (!mount) return null;

  // Loading / error shells (chrome themed by darkMode; wrapped in .dark so Tailwind
  // dark: variants resolve inside the popup document).
  const shell = (inner) => createPortal(
    <div className={darkMode ? 'dark' : ''} style={{ height: '100%' }}>
      <div className={`flex h-full w-full flex-col overflow-hidden ${reportModalPanelClass(darkMode)}`}>
        {inner}
      </div>
    </div>,
    mount,
  );

  if (win.status === 'loading') {
    // A group report runs through the async job path and can take minutes, so a
    // bare spinner is indistinguishable from a hang. members_total is 0 until
    // the server has resolved the member list, and stays 0 for single reports.
    const done = win.progress?.members_done ?? 0;
    const total = win.progress?.members_total ?? 0;
    return shell(
      <div className={`flex flex-1 flex-col items-center justify-center gap-2 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
        <div className="flex items-center gap-3">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>Generating {win.reportName}...</span>
        </div>
        {total > 0 && (
          <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
            {done} of {total} accounts
          </span>
        )}
      </div>
    );
  }
  if (win.status === 'error') {
    return shell(
      <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center">
        <div className={`text-sm ${darkMode ? 'text-red-400' : 'text-red-600'}`}>{win.error || 'Failed to generate report'}</div>
        <button type="button" onClick={() => closeReportWindow(win.id)} className="rounded bg-teal-600 px-3 py-1 text-xs text-white hover:bg-teal-500">Close</button>
      </div>
    );
  }

  // status === 'ready'
  return (
    <>
      {createPortal(
        <div className={darkMode ? 'dark' : ''} style={{ height: '100%' }}>
          <div className={`flex h-full w-full flex-col overflow-hidden ${reportModalPanelClass(darkMode)}`}>
            {/* Toolbar (no drag handle, no min/max/close - the OS window owns those) */}
            <div className={`flex items-center justify-between gap-2 px-3 py-2 border-b select-none ${reportModalHeaderBgClass(darkMode)} ${reportModalBorderClass(darkMode)}`}>
              <div className="min-w-0">
                <div className={`truncate text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{win.reportName}</div>
                {subtitle && <div className={`truncate text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{subtitle}</div>}
                {hiddenIndexes.size > 0 && (
                  <button
                    type="button"
                    onClick={() => setHiddenIndexes(new Set())}
                    className={`mt-0.5 inline-flex items-center gap-1 text-xs ${darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`}
                    title="Show all hidden benchmarks"
                  >
                    <span>{hiddenIndexes.size} benchmark{hiddenIndexes.size === 1 ? '' : 's'} hidden</span>
                    <span aria-hidden="true">-</span>
                    <span className="underline">Show all</span>
                  </button>
                )}
              </div>
              <div className="flex items-center gap-0.5 shrink-0">
                {win.reportType && (
                  <>
                    <button
                      type="button"
                      title="Rerun with different parameters"
                      aria-pressed={rerunOpen}
                      className={`${iconBtn} ${rerunOpen ? (darkMode ? 'text-white bg-white/10' : 'text-gray-900 bg-gray-200') : ''}`}
                      onClick={() => { setRerunError(null); setRerunOpen((v) => !v); }}
                    >
                      <SlidersHorizontal className="h-4 w-4" />
                    </button>
                    <span className={`mx-1 h-4 w-px ${darkMode ? 'bg-white/10' : 'bg-gray-300'}`} aria-hidden="true" />
                  </>
                )}
                {/* Zoom: out / readout (click = reset) / in. A multiplier on the viewer's
                    auto-fit page scale - 100% fits one page to the window; out shrinks to
                    see more pages, in magnifies to read. On-screen only: the print and
                    xlsx-export DOMs are separate subtrees and stay unzoomed. */}
                <button
                  type="button"
                  title="Zoom out"
                  className={`${iconBtn} disabled:opacity-40 disabled:pointer-events-none`}
                  onClick={() => setZoom((z) => stepZoom(z, -1))}
                  disabled={!canZoomOut(zoom)}
                >
                  <ZoomOut className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  title="Reset zoom to 100%"
                  className={`px-1 text-xs tabular-nums text-center ${
                    darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'
                  }`}
                  style={{ minWidth: '3.5ch' }}
                  onClick={() => setZoom(ZOOM_RESET)}
                >
                  {zoomPercent(zoom)}
                </button>
                <button
                  type="button"
                  title="Zoom in"
                  className={`${iconBtn} disabled:opacity-40 disabled:pointer-events-none`}
                  onClick={() => setZoom((z) => stepZoom(z, 1))}
                  disabled={!canZoomIn(zoom)}
                >
                  <ZoomIn className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  title={showLogo ? 'Hide firm logo' : 'Show firm logo'}
                  aria-pressed={showLogo}
                  className={`${iconBtn} ${
                    showLogo ? (darkMode ? 'text-white bg-white/10' : 'text-gray-900 bg-gray-200') : ''
                  }`}
                  onClick={() => setShowLogo((v) => !v)}
                >
                  <Image className="h-4 w-4" />
                </button>
                {/* Cents vs whole dollars for every money column of this report.
                    data-export-skip for consistency with the in-report controls;
                    the toolbar is window chrome and never reaches the export or
                    print DOM anyway. */}
                <button
                  type="button"
                  data-export-skip
                  title={moneyPrecision === 'cents' ? 'Round amounts to whole dollars' : 'Show cents on amounts'}
                  aria-pressed={moneyPrecision === 'cents'}
                  className={`${iconBtn} ${
                    moneyPrecision === 'cents' ? (darkMode ? 'text-white bg-white/10' : 'text-gray-900 bg-gray-200') : ''
                  }`}
                  onClick={() => setMoneyOverride(moneyPrecision === 'cents' ? 'whole' : 'cents')}
                >
                  <DollarSign className="h-4 w-4" />
                </button>
                <span
                  className={`mx-1 h-4 w-px ${darkMode ? 'bg-white/10' : 'bg-gray-300'}`}
                  aria-hidden="true"
                />
                {/* Open the field-insight chat without clicking a field. Toggles the
                    panel and refocuses its input. */}
                <button
                  type="button"
                  title={insightOpen ? 'Hide chat' : 'Chat'}
                  aria-pressed={insightOpen}
                  className={`${iconBtn} ${
                    insightOpen ? (darkMode ? 'text-white bg-white/10' : 'text-gray-900 bg-gray-200') : ''
                  }`}
                  onClick={() => { setInsightOpen((v) => !v); setFocusNonce((n) => n + 1); }}
                >
                  <MessageSquare className="h-4 w-4" />
                </button>
                {win.requestParams && (
                  <div className="relative">
                    <button
                      type="button"
                      title="Save configuration"
                      aria-pressed={saveOpen}
                      className={`${iconBtn} ${saveOpen ? (darkMode ? 'text-white bg-white/10' : 'text-gray-900 bg-gray-200') : ''}`}
                      onClick={openSavePopover}
                    >
                      <Save className="h-4 w-4" />
                    </button>
                    {saveOpen && (
                      <div
                        className={`absolute right-0 top-9 z-30 w-64 rounded-md border p-3 text-left shadow-2xl ${
                          darkMode ? 'bg-[#1c1c1c] border-white/10' : 'bg-white border-gray-200'
                        }`}
                        // Keep clicks/drags inside the popover from reaching the
                        // titlebar's drag handle or the window's onMouseDown focus logic.
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        <label
                          htmlFor={`save-config-name-${win.id}`}
                          className={`mb-1 block text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}
                        >
                          Save configuration as
                        </label>
                        <input
                          id={`save-config-name-${win.id}`}
                          type="text"
                          autoFocus
                          value={saveName}
                          onChange={(e) => setSaveName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveConfig();
                            if (e.key === 'Escape') setSaveOpen(false);
                          }}
                          className={`mb-2 w-full rounded border px-2 py-1 text-sm focus:outline-none ${
                            darkMode ? 'bg-[#141414] border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900'
                          }`}
                        />
                        {saveStatus === 'error' && (
                          <div className="mb-2 text-xs text-red-500">{saveErrorMessage}</div>
                        )}
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            className={`rounded px-2 py-1 text-xs ${darkMode ? 'text-gray-300 hover:bg-white/10' : 'text-gray-600 hover:bg-gray-100'}`}
                            onClick={() => setSaveOpen(false)}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            className="rounded bg-teal-600 px-2 py-1 text-xs text-white hover:bg-teal-500 disabled:opacity-50"
                            disabled={saveStatus === 'saving' || !saveName.trim()}
                            onClick={handleSaveConfig}
                          >
                            {saveStatus === 'saving' ? 'Saving...' : 'Save'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <button
                  type="button"
                  title="Export to spreadsheet"
                  className={iconBtn}
                  onClick={() => {
                    if (moneyPrecision === 'cents') {
                      exportReportToXlsx(sourceRef.current, win.reportName).catch((e) =>
                        console.error('Report export failed', e)
                      );
                      return;
                    }
                    setExporting(true);
                  }}
                >
                  <FileSpreadsheet className="h-4 w-4" />
                </button>
                <button type="button" title="Print" className={iconBtn} onClick={() => setPrinting(true)}>
                  <Printer className="h-4 w-4" />
                </button>
              </div>
            </div>

            {rerunError && (
              <div className={`flex items-center justify-between gap-2 border-b px-3 py-1.5 text-xs ${
                darkMode ? 'border-white/10 bg-red-500/10 text-red-400' : 'border-gray-300 bg-red-50 text-red-600'
              }`}>
                <span className="truncate">{rerunError}</span>
                <button type="button" className="shrink-0 underline" onClick={() => setRerunError(null)}>Dismiss</button>
              </div>
            )}

            {/* The report's own configuration dialog, rendered INSIDE this window
                (it is a fixed-inset overlay, so it covers the report here rather
                than the main tab the PM is not looking at).

                No `initialParams`: a rerun is normally the SAME report for a
                different portfolio, so the dialog opens on the date this report
                was generated for (the dialog reads it from
                lib/lastGeneratedDates) and every other field - portfolio
                included - at its default, ready to be re-picked. */}
            <ReportConfigDialog
              reportType={win.reportType}
              reportName={template ? template.name : reportTitleForType(win.reportType)}
              reportDescription={template ? template.description : ''}
              isOpen={rerunOpen}
              onClose={closeRerun}
              onConfirm={handleRerun}
              darkMode={darkMode}
            />

            {/* Body. Every report renders live in the continuous viewer (one scroll,
                frozen title band + column headers); the body is forced-light so it
                reads as paper. Group "@" reports get a member tab bar in the window
                chrome (above the viewer); a tab SCROLLS to that portfolio and
                scroll-spy highlights the active one - no content swap. */}
            <div className="flex flex-1 flex-col overflow-hidden">
              {groupMembers && (
                <div
                  className={`flex gap-1 overflow-x-auto border-b px-2 py-1 ${darkMode ? 'border-white/10 bg-[#141414]' : 'border-gray-300 bg-gray-50'}`}
                >
                  {groupMembers.map((m, i) => {
                    const activeIdx = Math.min(activeMember, groupMembers.length - 1);
                    // A degraded group keeps the failed member's tab so the tab
                    // order still matches the sections, but the PM has to be
                    // able to see WHICH accounts are missing without scrolling
                    // the whole document.
                    const failed = m.error != null;
                    return (
                      <button
                        key={i}
                        type="button"
                        onClick={() => scrollToMember(i)}
                        title={failed ? m.error : undefined}
                        className={`rounded px-3 py-1 text-xs whitespace-nowrap ${
                          i === activeIdx
                            ? 'bg-teal-600 text-white'
                            : failed
                              ? darkMode ? 'bg-[#2a1414] text-red-300 hover:bg-[#331818]' : 'bg-red-50 text-red-700 hover:bg-red-100'
                              : darkMode ? 'bg-[#1c1c1c] text-gray-300 hover:bg-[#242424]' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {failed ? '! ' : ''}{m.account_header?.account_name || `Account ${i + 1}`}
                      </button>
                    );
                  })}
                </div>
              )}
              {/* relative wrapper so the field-insight panel can overlay the viewer's
                  right side without entering the report subtree (its clicks must not
                  reach the viewer's field delegation). */}
              <div className="relative flex-1 overflow-hidden">
                <ContinuousReportViewer
                  sourceRef={sourceRef}
                  zoom={zoom}
                  alignLeft={insightOpen}
                  // 20px gap + 300px min panel + 20px right inset: the fit yields this
                  // strip so the panel never overlaps a fit-zoomed report in a windowed
                  // (non-maximized) report.
                  reserveRight={insightOpen ? 340 : 0}
                  onPageLayout={onPageLayout}
                  memberCount={groupMembers ? groupMembers.length : 1}
                  onActiveMemberChange={setActiveMember}
                  scrollRequest={scrollRequest}
                  onBodyToggle={toggleRow}
                  onToggleAll={toggleAll}
                  onFieldSingleClick={onFieldSingleClick}
                  onFieldDoubleClick={onFieldDoubleClick}
                >
                  <ThemeContext.Provider value={{ darkMode: false, toggleDarkMode: () => {} }}>
                    {reportBody()}
                  </ThemeContext.Provider>
                </ContinuousReportViewer>
                {insightOpen && (
                  <div
                    className="absolute flex justify-center"
                    // The panel strip: from just right of the rendered pages to the
                    // window edge (measured via onPageLayout so it tracks zoom / fit /
                    // resize), clamped to keep a usable minimum width at high zoom.
                    // Inset from the top (clears the viewer's control bar) and bottom.
                    // Click-through wrapper: the panel's transparent chrome lets
                    // clicks reach report fields underneath, and the panel re-enables
                    // pointer events on its own messages/input/minimize.
                    style={{
                      top: 48,
                      bottom: 28,
                      right: 20,
                      ...(pageLayout
                        ? {
                            left: Math.max(
                              0,
                              Math.min(pageLayout.pageRightEdge + 20, pageLayout.viewportWidth - 320)
                            ),
                          }
                        : { width: 'min(420px, 45%)' }),
                      zIndex: 20,
                      pointerEvents: 'none',
                      transition: 'left 200ms ease',
                    }}
                  >
                    {/* Center the panel within the strip (spec: centered in the space
                        the report does not cover). */}
                    <div style={{ position: 'relative', height: '100%', width: '100%', maxWidth: 480, pointerEvents: 'none' }}>
                      <FieldInsightPanel
                        sessionKey={win.id}
                        darkMode={false}
                        visible={insightOpen}
                        focusNonce={focusNonce}
                        onMinimize={() => setInsightOpen(false)}
                        fetchWithAuth={fetchWithAuth}
                        recordActivity={recordActivity}
                        placeholder="Ask about this report..."
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>,
        mount,
      )}

      {/* Print source: a forced-light copy of the report body rendered offscreen.
          The print effect paged.js-paginates it into #report-print-root, then moves
          the finished pages into the report's own OS window for preview (one
          landscape page per sheet, header on each). */}
      {printing &&
        createPortal(
          <ThemeContext.Provider value={{ darkMode: false, toggleDarkMode: () => {} }}>
            {/* One forced-light source per portfolio; the print effect paginates each
                separately (append) so every member carries its own per-page header. */}
            <div className="absolute -left-[99999px] top-0 w-[1056px] bg-white text-black" aria-hidden>
              {printMembers.map((m, i) => (
                <div key={i} id={`report-print-source-${i}`}>
                  <ReportDisplayModal
                    embedded
                    isOpen
                    onClose={() => {}}
                    reportData={{ ...(m.data || {}), ...optionFlags }}
                    reportType={win.reportType}
                    reportName={win.reportName}
                    showLogo={showLogo}
                    // The firm mark belongs on page 1 of the DOCUMENT, so only the
                    // first member's source carries it: a group print paginates the
                    // members back-to-back into one continuous page sequence.
                    brand={i === 0 ? brand : null}
                    // Print reflects the on-screen expanded lot rows, yield
                    // toggles and money precision for this member (no onChange:
                    // the controls live in the window chrome / are stripped from
                    // the print DOM via data-export-skip).
                    expandedRows={expandedByMember[i]}
                    moneyPrecision={moneyPrecision}
                    yocBasis={yocBasis}
                    showMarketYield={showMarketYield}
                    includeCashYield={includeCashYield}
                    feeBasis={feeBasis}
                    accruedBasis={accruedBasis}
                    holdingsBasis={holdingsBasis}
                    securityFilter={securityFilter}
                    returnBasis={returnBasis}
                    hiddenIndexes={hiddenIndexes}
                    selectedIndexNames={selectedIndexNames}
                  />
                </div>
              ))}
            </div>
          </ThemeContext.Provider>,
          document.body
        )}

      {/* Full-precision export source, so the spreadsheet always carries the real
          cents no matter what the display toggle says. Now that whole dollars is
          the DEFAULT, essentially every export comes through here rather than the
          rare toggled case it was built for.

          Each member is wrapped in the same `.report-member[data-export-sheet]`
          section the on-screen document uses, because that is what
          reportTablesToSheets keys on to emit ONE SHEET PER PORTFOLIO. Without
          the wrapper it takes its single-report branch and the whole group lands
          in one unnamed set, names colliding and silently deduped. That was a
          latent bug reachable only by toggling to whole dollars; making whole
          dollars the default would have made it the normal case. */}
      {exporting &&
        createPortal(
          <div className="absolute -left-[99999px] top-0 w-[1056px]" aria-hidden>
            <div id="report-export-source">
              {printMembers.map((m, i) => (
                <section
                  key={i}
                  className="report-member"
                  data-export-sheet={(m.account_header?.account_name) || `Account ${i + 1}`}
                >
                <ReportDisplayModal
                  embedded
                  isOpen
                  onClose={() => {}}
                  reportData={{ ...(m.data || {}), ...optionFlags }}
                  reportType={win.reportType}
                  reportName={win.reportName}
                  // The spreadsheet is data, not letterhead - no firm mark.
                  brand={null}
                  expandedRows={expandedByMember[i]}
                  moneyPrecision="cents"
                  yocBasis={yocBasis}
                  showMarketYield={showMarketYield}
                  includeCashYield={includeCashYield}
                  feeBasis={feeBasis}
                  accruedBasis={accruedBasis}
                  holdingsBasis={holdingsBasis}
                  securityFilter={securityFilter}
                  returnBasis={returnBasis}
                  hiddenIndexes={hiddenIndexes}
                  selectedIndexNames={selectedIndexNames}
                />
                </section>
              ))}
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default ReportPopup;
